/* ==========================================================================
   Bundle PDP video tutorials — the thumbnail modal.

   Progressive enhancement over three plain links. Without this file every
   thumbnail still opens its video on YouTube in a new tab; with it, the click
   is intercepted and the video plays in a modal on the page instead.

   The iframe is built at open time and thrown away at close time. That is
   deliberate on both ends: nothing is fetched from YouTube until a visitor
   actually asks for a video, and removing the element is the only reliable
   way to stop playback without talking to the player API.

   Scroll locking deliberately does NOT reuse the theme's own `.scroll-lock`
   class. That one pins the body with position: fixed, which collapses the
   document and drops it to offset 0 while the modal is up; the offset then has
   to be pushed back by hand on close, and that restore is what a visitor sees
   as the page jumping to the top and travelling back down to this section.
   Nothing here moves the document at all — see lockScroll below.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  /* The root element simply stops scrolling, so the page is still exactly
     where it was when the modal closes and there is no offset to stash or
     restore.

     Two details keep that honest. The width the scrollbar gives up is handed
     to the body as padding while it is gone, so a desktop page does not shift
     sideways as the bar disappears. And the offset is still recorded, purely
     as a repair: if a browser ever does drop the scroll position behind an
     overflow: hidden root, unlockScroll puts it back — a no-op everywhere it
     is not needed.

     Touch is handled in setup(): iOS Safari scrolls the page regardless of
     overflow: hidden, so a touchmove over the modal is cancelled outright. */
  function lockScroll(state) {
    var root = document.documentElement;
    var gap = window.innerWidth - root.clientWidth;

    state.scrollY = window.pageYOffset;
    state.padRight = document.body.style.paddingRight;
    if (gap > 0) document.body.style.paddingRight = gap + 'px';

    root.classList.add('bundle-vt-locked');
  }

  function unlockScroll(state) {
    document.documentElement.classList.remove('bundle-vt-locked');
    document.body.style.paddingRight = state.padRight || '';

    // Only ever fires on a browser that lost the offset on its own; the
    // ordinary path leaves the scroll position untouched.
    if (Math.abs(window.pageYOffset - (state.scrollY || 0)) > 1) {
      window.scrollTo(0, state.scrollY || 0);
    }
  }

  function buildIframe(embed, title) {
    var iframe = document.createElement('iframe');

    // The embed URLs Liquid hands over never carry a query of their own, so
    // the parameters can simply be appended.
    iframe.src =
      embed +
      '?autoplay=1&rel=0&modestbranding=1&playsinline=1' +
      '&title=' + encodeURIComponent(title || '');
    iframe.title = title || 'Video player';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', 'allowfullscreen');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

    return iframe;
  }

  function setup(section) {
    var modal = section.querySelector('[data-vt-modal]');
    var dialog = section.querySelector('[data-vt-dialog]');
    var player = section.querySelector('[data-vt-player]');
    var closeButton = section.querySelector('.bundle-vt__close');
    if (!modal || !dialog || !player) return;

    var state = { open: false, scrollY: 0, padRight: '', opener: null };

    function close() {
      if (!state.open) return;
      state.open = false;

      modal.hidden = true;
      // Emptying the node destroys the iframe, which is what stops the audio.
      player.innerHTML = '';
      unlockScroll(state);

      if (state.opener && document.contains(state.opener)) {
        state.opener.focus({ preventScroll: true });
      }
      state.opener = null;
    }

    function open(trigger) {
      var embed = trigger.getAttribute('data-vt-embed');
      if (!embed) return;

      state.opener = trigger;
      state.open = true;

      player.appendChild(buildIframe(embed, trigger.getAttribute('data-vt-title')));
      lockScroll(state);
      modal.hidden = false;

      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    section.querySelectorAll('[data-vt-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        // Anything that asks for a new tab or window — a modified click, a
        // middle click — is left to the browser, since the link is real.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof event.button === 'number' && event.button !== 0) return;

        event.preventDefault();
        open(trigger);
      });
    });

    modal.querySelectorAll('[data-vt-close]').forEach(function (node) {
      node.addEventListener('click', close);
    });

    // The overflow: hidden root is enough on a desktop browser but not on
    // iOS, which scrolls the page behind it anyway. The modal covers the whole
    // viewport and holds nothing scrollable, so a single-finger drag on it can
    // be cancelled outright; two fingers are left alone so pinch zoom still
    // works, and touches inside the cross-origin player never reach here.
    modal.addEventListener(
      'touchmove',
      function (event) {
        if (!state.open) return;
        if (event.touches && event.touches.length > 1) return;
        if (event.cancelable) event.preventDefault();
      },
      { passive: false }
    );

    document.addEventListener('keydown', function (event) {
      if (!state.open) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      // Keep the tab ring inside the dialog. The iframe counts as one stop,
      // so focus cycles between it and the close button rather than escaping
      // into the page behind the scrim.
      var stops = Array.prototype.filter.call(
        dialog.querySelectorAll(FOCUSABLE),
        function (node) {
          return node.offsetWidth > 0 || node.offsetHeight > 0;
        }
      );
      if (!stops.length) return;

      var first = stops[0];
      var last = stops[stops.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // The theme editor swaps a section's markup out from under us; drop the
    // lock rather than leaving the body pinned with nothing on top of it.
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target === section || event.target.contains(section)) close();
    });
  }

  function init(scope) {
    (scope || document)
      .querySelectorAll('[data-bundle-video-tutorials]')
      .forEach(function (section) {
        if (section.dataset.vtReady === 'true') return;
        section.dataset.vtReady = 'true';
        setup(section);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
